import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
} from '@mui/material';
import {
  People as PeopleIcon,
  Task as TaskIcon,
  TrendingUp as LeadsIcon,
  AttachMoney as MoneyIcon,
  Dashboard as DashboardIcon,
} from '@mui/icons-material';
import { WidgetCard } from './WidgetCard';
import { useDashboardStats } from '../../../hooks/useDashboardStats';

interface StatItemProps {
  label: string;
  value: number | string;
  icon: React.ReactNode;
  color: string;
  subtitle?: string;
}

const StatItem: React.FC<StatItemProps> = ({ label, value, icon, color, subtitle }) => (
  <Paper
    elevation={0}
    sx={{
      p: 1.5,
      textAlign: 'center',
      bgcolor: `${color}.light`,
      borderRadius: 2,
      height: '100%',
      opacity: 0.9,
    }}
  >
    <Box sx={{ color: 'white', mb: 0.5 }}>
      {icon}
    </Box>
    <Typography variant="h5" fontWeight="bold" color="white">
      {value}
    </Typography>
    <Typography variant="caption" color={`${color}.dark`} display="block">
      {label}
    </Typography>
    {subtitle && (
      <Typography variant="caption" color="white" fontWeight={500}>
        {subtitle}
      </Typography>
    )}
  </Paper>
);

export const QuickStatsWidget: React.FC = () => {
  const { data, isLoading, error, refetch } = useDashboardStats();

  const stats = data?.data;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <WidgetCard
      title="Quick Stats"
      icon={<DashboardIcon color="primary" />}
      isLoading={isLoading}
      error={error as Error | null}
      onRefresh={() => refetch()}
      minHeight={150}
    >
      <Grid container spacing={1.5}>
        <Grid item xs={6} sm={3}>
          <StatItem
            label="Clients"
            value={stats?.clients.total || 0}
            icon={<PeopleIcon />}
            color="primary"
            subtitle={`${stats?.clients.active || 0} active`}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatItem
            label="Tasks Today"
            value={stats?.tasks.today || 0}
            icon={<TaskIcon />}
            color="info"
            subtitle={stats?.tasks.overdue ? `${stats.tasks.overdue} overdue` : undefined}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatItem
            label="New Leads"
            value={stats?.leads.thisMonth || 0}
            icon={<LeadsIcon />}
            color="success"
            subtitle="this month"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatItem
            label="Pending"
            value={stats?.invoices.pending || 0}
            icon={<MoneyIcon />}
            color="warning"
            subtitle={stats?.invoices.pendingAmount ? formatCurrency(stats.invoices.pendingAmount) : undefined}
          />
        </Grid>
      </Grid>
    </WidgetCard>
  );
};

export default QuickStatsWidget;
